import { useCallback, useEffect, useRef, useState } from 'react';
import { sendChatAttachment } from '../../services/messaging/chatAttachmentSendService';

export const useChatAttachmentSend = ({ context, onMessageSent, onSuccess, setSending, setError }) => {
  const [retryReceipt, setRetryReceipt] = useState(null);
  const [activity, setActivity] = useState(null);
  const scopeKey = `${context.conversationId || ''}\u0000${context.currentUserId || ''}`;
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;

  const sendFile = useCallback(async (file, receipt = null) => {
    if (!file || !context.conversationId || !context.currentUserId) return;
    const frozenContext = receipt?.context || { ...context, tags: [...(context.tags || [])] };
    const requestScopeKey = `${frozenContext.conversationId || ''}\u0000${frozenContext.currentUserId || ''}`;
    try {
      setSending(true);
      setActivity({ phase: receipt ? 'retrying' : 'preparing', fileName: file.name || 'attachment' });
      const result = await sendChatAttachment({
        file,
        context: frozenContext,
        retryPayload: receipt?.retryPayload || null,
        onPhaseChange: (nextActivity) => {
          if (scopeKeyRef.current === requestScopeKey) setActivity(nextActivity);
        },
      });
      if (scopeKeyRef.current !== requestScopeKey) return;
      if (!result.success) {
        setRetryReceipt(result.retryPayload ? { file, context: frozenContext, retryPayload: result.retryPayload, scopeKey: requestScopeKey } : null);
        setError(result.error || 'Failed to send attachment');
        return;
      }
      setRetryReceipt(null);
      setError(null);
      onMessageSent?.(result.sentMessage);
      onSuccess?.();
    } catch (error) {
      if (scopeKeyRef.current !== requestScopeKey) return;
      setRetryReceipt({ file, context: frozenContext, retryPayload: receipt?.retryPayload || null });
      setError(error.message || 'Failed to send attachment');
    } finally {
      if (scopeKeyRef.current === requestScopeKey) {
        setActivity(null);
        setSending(false);
      }
    }
  }, [context, onMessageSent, onSuccess, setError, setSending]);

  const clearRetry = useCallback(() => setRetryReceipt(null), []);
  useEffect(() => {
    setRetryReceipt(null);
    setActivity(null);
    setSending(false);
  }, [scopeKey, setSending]);

  const scopedRetryReceipt = retryReceipt?.scopeKey === scopeKey ? retryReceipt : null;

  return {
    hasRetry: Boolean(scopedRetryReceipt),
    retryFileName: scopedRetryReceipt?.file?.name || '',
    activity,
    sendFile,
    retry: () => scopedRetryReceipt && sendFile(scopedRetryReceipt.file, scopedRetryReceipt),
    clearRetry,
  };
};
